import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Sparkles, Target, Zap, Users, Heart } from 'lucide-react';
import AppNav from '@/components/AppNav';
import SEO from '@/components/SEO';

const VALUES = [
  {
    icon: <Target className="w-5 h-5" />,
    title: 'Built for TikTok & UGC, not generic AI',
    body: 'Every prompt is tuned for TikTok Shop creators and UGC scripts — hooks that stop the scroll, scripts that match native pacing, visual angles that work on a phone screen.',
  },
  {
    icon: <Zap className="w-5 h-5" />,
    title: 'Speed that respects your day',
    body: 'One generation, one click, everything in under 20 seconds. No five-step wizard, no five-tab browser dance.',
  },
  {
    icon: <Users className="w-5 h-5" />,
    title: 'A creator-first commission model',
    body: 'Our 3-tier affiliate programme pays 30% on direct referrals, 7% on tier 2, and 3% on tier 3 — for life of the customer, not 30 days.',
  },
  {
    icon: <Heart className="w-5 h-5" />,
    title: 'Personal, not generic',
    body: 'Tell us a bit about you — age, gender, kids, pets — and the AI quietly adjusts tone and references so scripts sound like you, not a corporate brand.',
  },
];

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-background" data-testid="about-page">
      <SEO title="About" description="Poettik is an AI script generator built for UK TikTok Shop affiliates and UGC creators. Discover the story and the team behind the product." />
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary/10 rounded-full blur-3xl animate-pulse-glow" />
        <div className="absolute bottom-20 -left-40 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
      </div>

      <nav className="relative z-50 border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <AppNav />
          </div>
        </div>
      </nav>

      <main className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        {/* Hero */}
        <section className="text-center mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-2 text-xs text-primary bg-primary/10 px-3 py-1.5 rounded-full mb-4">
            <Sparkles className="w-3.5 h-3.5" />
            Our story
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold mb-4">
            We build tools for <span className="gradient-text">TikTok Shop &amp; UGC creators</span>
          </h1>
          <p className="text-muted-foreground text-base sm:text-lg max-w-xl mx-auto">
            Poettik exists because writing hooks and UGC scripts at 11pm on a Tuesday shouldn&apos;t feel like a second job.
          </p>
        </section>

        {/* Mission */}
        <section className="mb-12 sm:mb-16">
          <Card variant="glass" className="p-6 sm:p-8">
            <CardContent className="p-0 space-y-4 text-foreground/90 leading-relaxed">
              <p className="text-base sm:text-lg">
                TikTok Shop is the biggest live shopping wave in a decade — and UGC has become the way brands actually run paid ads. Either way, the unsung heroes are the creators. People filming in their kitchens, on their commute, between school runs, turning a product they actually love into a video that stops the scroll.
              </p>
              <p className="text-base sm:text-lg">
                We built Poettik so those creators stop staring at a blank screen. Drop in a product, get a complete content pack — hooks, visual angles, a tight 15-60 second script — generated by best-in-class AI and tuned with prompts that understand TikTok Shop and UGC, not just &quot;AI writing&quot;.
              </p>
              <p className="text-base sm:text-lg text-muted-foreground">
                We&apos;re a small, UK-based team. We obsess over the small details — the cadence of a hook, the way British creators actually talk, the way prompts behave when a product is niche or weird.
              </p>
            </CardContent>
          </Card>
        </section>

        {/* Values */}
        <section className="mb-12 sm:mb-16">
          <h2 className="font-display text-2xl sm:text-3xl font-bold mb-6 text-center">What we care about</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {VALUES.map((v, i) => (
              <Card key={i} variant="glass" className="p-5">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-primary/15 text-primary flex items-center justify-center shrink-0">
                    {v.icon}
                  </div>
                  <div>
                    <p className="font-semibold text-base mb-1">{v.title}</p>
                    <p className="text-sm text-muted-foreground leading-relaxed">{v.body}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="text-center">
          <Card variant="glass" className="inline-block p-6 sm:p-8">
            <p className="font-medium mb-1">Ready to try it?</p>
            <p className="text-sm text-muted-foreground mb-4">3-day free trial, no card required.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/register">
                <Button variant="premium" size="lg" data-testid="about-cta-trial">Start Free Trial</Button>
              </Link>
              <Link to="/pricing">
                <Button variant="outline" size="lg" data-testid="about-cta-pricing">See Pricing</Button>
              </Link>
            </div>
          </Card>
        </section>
      </main>
    </div>
  );
};

export default AboutPage;
